import * as React from 'react'
import { FC } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

import { TerritoryOverlap } from '@user/store/territory/overlapDetection'
import { TerritoryBoundary } from '@user/store/territory/typings'
import { useTerritoryOverlaps } from '@user/store/territory/hooks/useTerritoryOverlaps'

/** Never 0%: anything reported cleared a 1% floor, and "0%" would read as "nothing". */
const percent = (fraction: number): string =>
  `${Math.max(1, Math.round(fraction * 100))}%`

/**
 * One overlap in the words that make it actionable.
 *
 * Which number to lead with depends on the shape of the collision, and getting it wrong buries
 * the point: a new territory nested inside a big one is 79% of ITSELF and 4% of its host, while
 * a big territory swallowing a small one is the reverse. Each sentence names the fraction that
 * is alarming.
 */
export function describeOverlap(overlap: TerritoryOverlap): string {
  const { territoryName, sharedFractionOfDrawn, sharedFractionOfExisting } =
    overlap

  if (sharedFractionOfExisting >= 0.95) return `covers all of ${territoryName}`
  if (sharedFractionOfDrawn >= 0.95)
    return `sits entirely inside ${territoryName}`

  return sharedFractionOfDrawn >= sharedFractionOfExisting
    ? `${percent(sharedFractionOfDrawn)} of it is already in ${territoryName}`
    : `it covers ${percent(sharedFractionOfExisting)} of ${territoryName}`
}

type Props = {
  /** The shape being drawn or redrawn. */
  boundary: TerritoryBoundary
  /** The territory being redrawn, which must not be reported as overlapping itself. */
  excludeTerritoryId?: number | null
}

/**
 * Warns that a drawn shape lands on territory somebody already has (WWM-17508).
 *
 * Advisory, never a block. Overlapping territories are legal and sometimes deliberate -- two
 * reps working the same dense block, say -- so this does not disable saving. What it fixes is
 * that the consequence is otherwise invisible: assignment returns the FIRST rule matching by
 * `Order`, so a lead inside two territories goes to whichever rule happens to sit higher, and
 * the other owner never learns the lead existed. That was a real support thread on this POC
 * before anything drew attention to it.
 *
 * Silent when the shape is clean. A panel that says "no overlaps" on every territory trains
 * people to stop reading it, and there is no decision to make when there is nothing to report.
 */
export const TerritoryOverlapWarning: FC<Props> = ({
  boundary,
  excludeTerritoryId,
}) => {
  const { overlaps, status } = useTerritoryOverlaps(
    boundary,
    excludeTerritoryId,
  )

  if (status === 'checking' && !overlaps.length) {
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 2 }}
        data-test-id="territory-overlap-checking"
      >
        Checking for overlap with existing territories…
      </Typography>
    )
  }

  if (!overlaps.length) {
    // 'incomplete' with nothing found is the one case worth a word: the shape looks clear, but
    // some territories could not be compared, and claiming "clear" would overstate it.
    if (status === 'incomplete') {
      return (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 2 }}
          data-test-id="territory-overlap-incomplete"
        >
          Could not check every territory for overlap. You can still save.
        </Typography>
      )
    }

    return null
  }

  return (
    <Alert severity="warning" sx={{ mb: 2 }} data-test-id="territory-overlap">
      <AlertTitle sx={{ mb: 0.5 }}>
        {overlaps.length === 1
          ? 'This area overlaps an existing territory'
          : `This area overlaps ${overlaps.length} existing territories`}
      </AlertTitle>

      <Box component="ul" sx={{ pl: 2, m: 0 }}>
        {overlaps.map((overlap) => (
          <Typography
            key={overlap.territoryId}
            component="li"
            variant="body2"
            data-test-id="territory-overlap-item"
          >
            {describeOverlap(overlap)}
          </Typography>
        ))}
      </Box>

      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
        Leads in the shared area go to whichever assignment rule is ordered
        first, so the other owner may never see them. You can still save this
        shape if the overlap is intended.
      </Typography>

      {status === 'incomplete' && (
        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
          Some territories could not be checked, so there may be more.
        </Typography>
      )}
    </Alert>
  )
}
